import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getLectures, deleteLecture } from "../../redux/services/lectureSlice";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import Table from "../../common/Table";
import ConfirmationPage from "../../common/ConfirmationPage";
import { LuPencilLine } from "react-icons/lu";
import { AiOutlineDelete } from "react-icons/ai";
import { ROUTES } from "../../constant";
import { getWorkshops } from "../../redux/services/workshopSlice";

const WorkshopLecture = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const getLecturesData = useSelector((state) => state?.lecture?.lectureData);
  const getWorkshopData = useSelector(
    (state) => state?.workshop?.workshopData?.workshop_data
  );

  const [confirm, setConfirm] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  // Pagination + search
  const [page, setPage] = useState(0);
  const [pageLimit, setPageLimit] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchData, setSearchData] = useState("");
  const [workshopFilter, setWorkshopFilter] = useState("");

  const limitOptions = [
    { value: 10, label: 10 },
    { value: 30, label: 30 },
    { value: 50, label: 50 },
    { value: 100, label: 100 },
  ];

  const getAllWorkshops = useCallback(async () => {
    try {
      await dispatch(
        getWorkshops({ page: page + 1, limit: pageLimit, searchData })
      );
    } catch (error) {}
  }, [dispatch, page, pageLimit, searchData]);

  useEffect(() => {
    getAllWorkshops();
  }, [getAllWorkshops]);

  const filterOptions = [
    { value: "", label: "All" },
    ...(getWorkshopData
      ? getWorkshopData?.map((workshop) => ({
          value: workshop?._id,
          label: workshop?.title,
        }))
      : ""),
  ];

  // Fetch all lectures (no filter)
  const getAllLectures = useCallback(async () => {
    await dispatch(
      getLectures({
        page: page + 1,
        limit: pageLimit,
        searchData,
        type: "workshop",
        workshopId: workshopFilter,
      })
    );
  }, [dispatch, page, pageLimit, searchData, workshopFilter]);

  useEffect(() => {
    getAllLectures();
  }, [getAllLectures, workshopFilter]);

  // Delete handler
  const handleDelete = async () => {
    const res = await dispatch(deleteLecture(deleteId));
    toast.success(res?.payload?.message || "Deleted successfully");
    setConfirm(false);
    getAllLectures();
  };

  const columns = [
    {
      header: "S/N",
      key: "index",
      render: (_, __, index) => page * pageLimit + index + 1,
    },
    { header: "Title", key: "title" },
    { header: "Subtitle", key: "subtitle" },
    { header: "Language", key: "language" },
    { header: "Subject", key: "subjectName" },
    {
      header: "isLocked",
      key: "isLocked",
      render: (isLocked) => (isLocked ? "True" : "False"),
    },
    {
      header: "Image",
      key: "image",
      render: (img) => (
        <img
          src={img}
          alt="lecture"
          className="w-20 h-14 object-cover rounded-xl"
        />
      ),
    },
    {
      header: "Link",
      key: "link",
      render: (link) => (
        <a
          href={link}
          target="_blank"
          rel="noreferrer"
          className="text-primary underline"
        >
          Visit
        </a>
      ),
    },
  ];

  const actions = [
    {
      icon: <LuPencilLine />,
      onClick: (row) =>
        navigate(`${ROUTES.WORKSHOPS.LECTURES_ADD_EDIT}?lectureId=${row._id}`, {
          state: { row },
        }),
    },
    {
      icon: <AiOutlineDelete />,
      onClick: (row) => {
        setDeleteId(row._id);
        setConfirm(true);
      },
    },
  ];

  return (
    <>
      <Table
        title="Workshop Lectures"
        buttonTitle="Add Workshop Lecture"
        showSearchbar={true}
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        setSearchData={setSearchData}
        onAddClick={() => navigate(ROUTES.WORKSHOPS.LECTURES_ADD_EDIT)}
        columns={columns}
        filterOptions={filterOptions}
        onFilterChange={(e) => {
          setWorkshopFilter(e ? e.value : "");
        }}
        data={getLecturesData?.lecture_data || []}
        actions={actions}
        pageCount={getLecturesData?.state?.page_limit}
        onPageChange={(selectedItem) => setPage(selectedItem.selected)}
        pageLimitOptions={limitOptions}
        selectedPageLimit={pageLimit}
        onPageLimitChange={setPageLimit}
      />

      {confirm && (
        <ConfirmationPage
          topicName="Workshop Lecture"
          confirm={confirm}
          onDelete={handleDelete}
          onCancel={() => setConfirm(false)}
        />
      )}
    </>
  );
};

export default WorkshopLecture;
